import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import InputDropdown from "../../../components/inputs/InputDropdown/InputDropdown";
import BarChartGraph from "../../../components/common/Charts/BarChartGraph";
import Table from "../../../components/common/Table/Table";
import { getApiInstance } from "../../../utils/api";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";
import "./HuellaCarbono.scss";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import { formatCurrency } from "../../../utils/utils";
import SimpleLineChartGraph from "../../../components/common/Charts/SimpleLineChartGraph";
import CardCharts from "../../../components/common/CardCharts/CardCharts";
import { getWeekNumber, getStartOfWeek } from "../../../utils/utils";
import { MESES_ES, DIAS_ES } from "../../../utils/utils";

export default function HuellaCarbono() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [informeData, setInformeData] = useState([]);
    const [activeTab, setActiveTab] = useState("grafica");

    const [tipoDesglose, setTipoDesglose] = useState("cadena");
    const [selectedAno, setSelectedAno] = useState("all");
    const [selectedObra, setSelectedObra] = useState("all");

    const opcionesDesglose = [
        { value: "cadena", label: "Desglose por Cadena" },
        { value: "tipoObra", label: "Desglose por Tipo de Obra" },
        { value: "pais", label: "Desglose por País" },
    ];

    useEffect(() => {
        const fetchHuellaCarbono = async () => {
            setLoading(true);
            setError(null);
            try {
                const api = getApiInstance();
                const response = await api.get(`/api/Informe/GetHuella`);
                setInformeData(response.data);
            } catch (err) {
                setError(
                    "No se ha podido cargar el informe. Vuelve a intentarlo más tarde."
                );
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchHuellaCarbono();
    }, []);

    const { anos, obras } = useMemo(() => {
        if (informeData.length === 0) return { anos: [], obras: [] };

        const anosUnicos = [
            ...new Set(informeData.map((item) => item.ano)),
        ].sort((a, b) => b - a);
        const obrasUnicas = informeData
            .map((item) => ({
                codObra: item.codObra,
                nombreObra: item.nombreObra,
            }))
            .filter(
                (v, i, a) => a.findIndex((t) => t.codObra === v.codObra) === i
            );

        return {
            anos: [
                { value: "all", label: "Todos los Años" },
                ...anosUnicos.map((a) => ({ value: a, label: a })),
            ],
            obras: [
                { value: "all", label: "Todas las Obras" },
                ...obrasUnicas.map((o) => ({
                    value: o.codObra,
                    label: `${o.codObra} - ${o.nombreObra}`,
                })),
            ],
        };
    }, [informeData]);

    const lineChartData = useMemo(() => {
        if (informeData.length === 0) return {};

        const ahora = new Date();
        const datosConFecha = informeData.map((d) => ({
            ...d,
            fecha: new Date(d.fechaTransporte),
        }));

        // Agrupar por AÑO (mostrando meses)
        const porMes = datosConFecha.reduce((acc, item) => {
            const mesIndex = item.fecha.getMonth(); // 0 para Enero, 1 para Febrero...
            acc[mesIndex] = (acc[mesIndex] || 0) + item.emisionesTotales;
            return acc;
        }, {});
        const dataAnual = MESES_ES.map((nombreMes, index) => ({
            name: nombreMes,
            emisiones: porMes[index] || 0,
        }));

        const inicioDelMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
        const datosUltimoMes = datosConFecha.filter(
            (d) => d.fecha >= inicioDelMes && d.fecha <= ahora
        );
        const porSemanaMes = datosUltimoMes.reduce((acc, item) => {
            const semana = getWeekNumber(item.fecha);
            acc[semana] = (acc[semana] || 0) + item.emisionesTotales;
            return acc;
        }, {});
        const dataMensual = Object.keys(porSemanaMes).map((semana) => ({
            name: `Sem ${semana}`,
            emisiones: porSemanaMes[semana],
        }));

        const inicioUltimaSemana = getStartOfWeek(ahora);
        inicioUltimaSemana.setHours(0, 0, 0, 0); // Normalizar a medianoche
        const datosUltimaSemana = datosConFecha.filter(
            (d) => d.fecha >= inicioUltimaSemana && d.fecha <= ahora
        );
        const porDia = datosUltimaSemana.reduce((acc, item) => {
            const diaIndex = item.fecha.getDay(); // 0 para Domingo, 1 para Lunes...
            acc[diaIndex] = (acc[diaIndex] || 0) + item.emisionesTotales;
            return acc;
        }, {});
        const diasOrdenados = [1, 2, 3, 4, 5, 6, 0]; // Lunes a Domingo
        const dataSemanal = diasOrdenados.map((diaIndex) => ({
            name: DIAS_ES[diaIndex],
            emisiones: porDia[diaIndex] || 0,
        }));

        return {
            chartData: {
                Año: dataAnual,
                Mes:
                    dataMensual.length > 0
                        ? dataMensual
                        : [{ name: "Sin datos", emisiones: 0 }],
                Semana: dataSemanal,
            },
        };
    }, [informeData]);

    const { datosGrafico, datosTabla, categoriaLabel } = useMemo(() => {
        if (informeData.length === 0) {
            return {
                datosGrafico: [],
                datosTabla: [],
                categoriaLabel: "Categoría",
            };
        }

        const datosFiltrados = informeData.filter((item) => {
            const anoMatch = selectedAno === "all" || item.ano === selectedAno;
            const obraMatch =
                selectedObra === "all" || item.codObra === selectedObra;
            return anoMatch && obraMatch;
        });

        const config = {
            cadena: { key: "cadena", label: "Cadena" },
            tipoObra: { key: "tipoObra", label: "Tipo de Obra" },
            pais: { key: "pais", label: "País" },
        }[tipoDesglose];

        const agrupado = datosFiltrados.reduce((acc, item) => {
            const groupName = item[config.key] || "Sin Asignar";
            if (!acc[groupName]) {
                acc[groupName] = {
                    emisionesCO2: 0,
                    emisionesCH4: 0,
                    emisionesN2O: 0,
                    emisionesTotales: 0,
                };
            }
            acc[groupName].emisionesCO2 += item.emisionesCO2;
            acc[groupName].emisionesCH4 += item.emisionesCH4;
            acc[groupName].emisionesN2O += item.emisionesN2O;
            acc[groupName].emisionesTotales += item.emisionesTotales;
            return acc;
        }, {});

        const tablaProcesada = Object.keys(agrupado).map((groupName) => ({
            categoria: groupName,
            ...agrupado[groupName],
        }));

        const graficoProcesado = tablaProcesada.map((item) => ({
            name: item.categoria,
            "CO2 (kg)": parseFloat(item.emisionesCO2.toFixed(2)),
            "CH4 (g)": parseFloat(item.emisionesCH4.toFixed(2)),
            "N2O (g)": parseFloat(item.emisionesN2O.toFixed(2)),
        }));

        return {
            datosGrafico: graficoProcesado,
            datosTabla: tablaProcesada,
            categoriaLabel: config.label,
        };
    }, [informeData, tipoDesglose, selectedAno, selectedObra]);

    const columnasTabla = [
        {
            key: "categoria",
            label: categoriaLabel,
            visible: true,
            sortable: true,
        },
        {
            key: "emisionesCO2",
            label: "Emisiones CO2 (kg)",
            type: "number",
            visible: true,
            sortable: true,
            render: (row) => row.emisionesCO2.toFixed(2),
        },
        {
            key: "emisionesCH4",
            label: "Emisiones CH4 (g)",
            type: "number",
            visible: true,
            sortable: true,
            render: (row) => row.emisionesCH4.toFixed(2),
        },
        {
            key: "emisionesN2O",
            label: "Emisiones N2O (g)",
            type: "number",
            visible: true,
            sortable: true,
            render: (row) => row.emisionesN2O.toFixed(2),
        },
        {
            key: "emisionesTotales",
            label: "Emisiones Totales (kg)",
            type: "number",
            visible: true,
            sortable: true,
            render: (row) => row.emisionesTotales.toFixed(2),
        },
    ];

    const tabs = [
        { id: "grafica", label: "Gráfica" },
        { id: "desglose", label: "Desglose" },
        { id: "listado", label: "Listado" },
    ];

    const renderDesgloseContent = () => (
        <div className="cantidades-generadas-content">
            <div className="graficas-card">
                <div className="charts-section">
                    <div className="chart-container bar-chart-container">
                        <div className="chart-header">
                            <h3>Costes</h3>
                            {/* <span className="total-amount">
                <strong>Real:</strong> {formatCurrency(totalCosteReal)}
              </span>
              <span className="total-amount">
                <strong>Previsto:</strong> {formatCurrency(totalCostePrevisto)}
              </span> */}
                        </div>
                        <div className="chart-wrapper">
                            <BarChartGraph
                                data={{ all: datosGrafico }}
                                activeTab="all"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderGraficaContent = () => (
        <div className="cantidades-generadas-content">
            <div className="graficas-card">
                <div className="charts-section">
                    <div className="chart-container bar-chart-container">
                        <div className="chart-header">
                            <h3>Costes</h3>
                            {/* <span className="total-amount">
                <strong>Real:</strong> {formatCurrency(totalCosteReal)}
              </span>
              <span className="total-amount">
                <strong>Previsto:</strong> {formatCurrency(totalCostePrevisto)}
              </span> */}
                        </div>
                        <CardCharts
                            title="Evolución temporal"
                            subtitle="Tendencia de generación"
                            data={lineChartData}
                        >
                            <SimpleLineChartGraph data={lineChartData} />
                        </CardCharts>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderListadoContent = () => (
        <div className="listado-content">
            <Table
                columns={columnasTabla}
                dataTable={datosTabla}
                keyProp="categoria"
                sortProp="categoria"
                noResultsMessage="No hay datos para los filtros seleccionados."
                elementName={categoriaLabel.toLowerCase()}
            />
        </div>
    );

    return (
        <div className="huella-carbono-page">
            {" "}
            <PageLayout
                title="Informe de Huella de Carbono"
                subtitle="Análisis de emisiones de CO2, CH4 y N2O"
            >
                <div className="filters-section-common">
                    <div className="filter-group">
                        <InputDropdown
                            options={opcionesDesglose}
                            value={opcionesDesglose.find(
                                (opt) => opt.value === tipoDesglose
                            )}
                            onChange={(option) => setTipoDesglose(option.value)}
                        />
                        <InputDropdown
                            options={anos}
                            value={anos.find(
                                (opt) => opt.value === selectedAno
                            )}
                            onChange={(option) => setSelectedAno(option.value)}
                        />
                        <InputDropdown
                            options={obras}
                            value={obras.find(
                                (opt) => opt.value === selectedObra
                            )}
                            onChange={(option) => setSelectedObra(option.value)}
                        />
                    </div>
                </div>

                <TabsNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                {error && <div className="error-message">{error}</div>}
                {loading ? (
                    <div className="loading-message">Cargando datos...</div>
                ) : activeTab === "desglose" ? (
                    renderDesgloseContent()
                ) : activeTab == "grafica" ? (
                    renderGraficaContent()
                ) : (
                    renderListadoContent()
                )}
            </PageLayout>
        </div>
    );
}
